import React, { useState } from 'react'
import { Dialog, Box } from '@mui/material'

import SpecEditor from '../../spec-editor/SpecEditor'
import useAccount from '../../../hooks/useAccount'
import { clearSpecEditorSessionState } from '../../../hooks/useSpecEditor'
import { resolveTableSaveRules } from '../../../utils/dashboardTableSave'

interface EditDataOverlayProps {
  open: boolean
  customer: string
  /**
   * The job of the table being edited (not necessarily the dashboard's
   * primary job on a multi-job dashboard, #1794). Fixed — SpecEditor takes it as a prop and has no
   * picker of its own, which is how the job and customer lock from #961 is
   * enforced here: there is nothing in the overlay that could change either.
   */
  job: string
  /** The dashboard being edited. Used to name the save folder (#968 Part B). */
  dashboardId: string
  dashboardName: string
  /** Table to open, or '' to start a new one. */
  tablePath: string
  tableTitle?: string
  /** Closes without saving. Always available — never trap the user in here. */
  onClose: () => void
  /**
   * Called with the saved table's path. The widget repoints at it, so the user
   * comes back to a chart already showing what they just built.
   */
  onSaved: (savedPath: string) => void
}

/**
 * Tier 2 (#968 Part B): the Tables spec editor, over the dashboard editor.
 *
 * Tier 1 shapes the view of a table that already has the numbers. When it
 * can't — a different base, new breaks, a filter the table doesn't contain —
 * this is the escape hatch, without leaving the dashboard.
 *
 * The overlay exists because SpecEditor stopped reading the router (#1033).
 * Run Spec and Save would otherwise navigate to the Tables page and discard
 * the dashboard being edited, so both are intercepted here instead.
 *
 * Save rules: editing an Exec/company table (or anyone else's) always forks
 * into this dashboard's own Tables/User/<email>/<dashboard-name>-<id> folder
 * — the authoritative table is never mutated. Editing the user's own table
 * offers overwrite (back into wherever it already lives) or fork (into the
 * dashboard's folder). resolveTableSaveRules is the single source of truth
 * for this; SpecEditor just writes wherever it says.
 */
export const EditDataOverlay: React.FC<EditDataOverlayProps> = ({
  open,
  customer,
  job,
  dashboardId,
  dashboardName,
  tablePath,
  tableTitle,
  onClose,
  onSaved,
}) => {
  const account = useAccount()
  const userEmail = account.user?.session_info?.email || ''

  // SpecEditor is keyed on the table it opened with, so reopening the overlay
  // on a different table starts the editor fresh rather than showing the last
  // one's axes.
  const [openedWith] = useState(tablePath)

  // A blank tablePath means "Create New" (#968 Part B): the empty state or a
  // fresh Edit Data click with no table selected yet. useSpecEditor already
  // has a sentinel for this — 'new' — used throughout the Tables page flow
  // for a table that doesn't exist on disk yet. Passing '' straight through
  // would instead trip onSaveSpec's "table path is missing" guard the first
  // time the user tries to save.
  const editorTable = openedWith === '' ? 'new' : openedWith

  const saveRules = resolveTableSaveRules(openedWith, { dashboardName, dashboardId, userEmail })

  // Leaving the overlay by ANY route — Cancel, backdrop, Escape, or after a
  // save — discards every cached editor key (draft + Run Spec preview) for
  // the table it was opened on. #968 Obs 7: unsaved Edit Spec changes used to
  // survive Cancel via sessionStorage and reappear on the next Edit Data.
  const handleClose = () => {
    clearSpecEditorSessionState(customer, job, editorTable)
    onClose()
  }

  // Only a genuine save reports saved: 'true'. (Run Spec no longer navigates
  // at all in the overlay — it previews inline, #968 Obs 5 — but the guard
  // stays: an /editspec-style navigation must never close the overlay or
  // repoint the widget unless something was actually written.)
  const handleNavigateToTables = (params: Record<string, string>) => {
    const savedPath = params.table
    if (params.saved === 'true' && savedPath) {
      clearSpecEditorSessionState(customer, job, editorTable)
      onSaved(savedPath)
      onClose()
    }
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      PaperProps={{ sx: { width: '95vw', height: '90vh', maxWidth: 1600 } }}
    >
      <Box sx={{ height: '100%', overflow: 'hidden' }}>
        <SpecEditor
          key={openedWith}
          customer={customer}
          job={job}
          table={editorTable}
          tableTitle={tableTitle}
          inlineRunPreview
          onCancel={handleClose}
          onNavigateToTables={handleNavigateToTables}
          saveConfig={{
            targetFolder: saveRules.targetFolder,
            originalFolder: saveRules.originalFolder,
            allowOverwrite: saveRules.allowOverwrite,
          }}
        />
      </Box>
    </Dialog>
  )
}

export default EditDataOverlay
